import { getChartPalette } from "design-system/lib/chart-utils"
import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import { AnimatePresence, motion } from "framer-motion"
import { UserRoundX } from "lucide-react"
import { getTextWidth } from "modules/shared/lib/charts"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, CartesianGrid, Cell, LabelList, LabelProps, XAxis, YAxis } from "recharts"

const LABEL_FONT_SIZE = 12
const LABEL_OFFSET = 8

interface SegmentGroupChartProps {
  chartData: {
    name: string
    value: number
  }[]
}

export function SegmentGroupSimpleChart({ chartData }: SegmentGroupChartProps) {
  const { t } = useTranslation()

  const chartConfig = {} as ChartConfig

  const totalCustomersCount = useMemo(() => {
    return chartData.reduce((acc, segment) => acc + segment.value, 0)
  }, [chartData])

  const palette = useMemo(() => {
    return getChartPalette("primary", "sequence", chartData.length)
  }, [chartData.length])

  if (chartData.length === 0) {
    return (
      <div className="flex flex-row gap-4 items-center justify-center min-h-24">
        <UserRoundX className="size-10" strokeWidth={1.25} />
        <div className="flex flex-col">
          <span className="text-md font-medium">{t("segments.list.empty.title")}</span>
          <span className="text-sm text-muted-foreground">
            {t("segments.list.empty.description")}
          </span>
        </div>
      </div>
    )
  }

  if (totalCustomersCount === 0) {
    return (
      <div className="flex flex-row gap-4 items-center justify-center min-h-24">
        <UserRoundX className="size-10" strokeWidth={1.25} />
        <div className="flex flex-col">
          <span className="text-md font-medium">{t("segments.list.emptyCustomers.title")}</span>
          <span className="text-sm text-muted-foreground">
            {t("segments.list.emptyCustomers.description")}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 h-[220px]">
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <ChartContainer config={chartConfig} className="aspect-auto h-[220px]">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 16, bottom: 5, left: 0 }}
              style={{ cursor: "pointer" }}
            >
              <CartesianGrid horizontal={false} />
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" hide />
              <Bar dataKey="value" radius={5} barSize={42} isAnimationActive={false}>
                {chartData.map((_, index) => {
                  const colorConfig = palette[index]
                  if (!colorConfig) return null
                  // Use color-mix to apply opacity to the color itself, not the element
                  // This prevents the opacity from affecting child elements like LabelList
                  const fillColor =
                    colorConfig.opacity === 1
                      ? `var(--${colorConfig.color})`
                      : `color-mix(in srgb, var(--${colorConfig.color}) ${colorConfig.opacity * 100}%, transparent)`
                  return <Cell key={`cell-${index}`} fill={fillColor} />
                })}
                <LabelList
                  dataKey="value"
                  content={(props) => (
                    <SegmentBarLabel {...props} name={chartData[props.index ?? 0]?.name ?? ""} />
                  )}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function SegmentBarLabel({ x, y, width, height, value, name }: LabelProps & { name: string }) {
  const barX = Number(x)
  const barWidth = Number(width)
  const centerY = Number(y) + Number(height) / 2
  const count = Number(value).toLocaleString()
  const isNameInside = getTextWidth(name, `${LABEL_FONT_SIZE}px Inter, sans-serif`) <= barWidth

  if (isNameInside) {
    return (
      <g fontSize={LABEL_FONT_SIZE}>
        <text
          x={barX + LABEL_OFFSET}
          y={centerY}
          dominantBaseline="central"
          className="fill-background dark:fill-foreground"
        >
          {name}
        </text>
        <text
          x={barX + barWidth + LABEL_OFFSET}
          y={centerY}
          dominantBaseline="central"
          className="fill-foreground"
        >
          {count}
        </text>
      </g>
    )
  }

  return (
    <text
      x={barX + barWidth + LABEL_OFFSET}
      y={centerY}
      dominantBaseline="central"
      fontSize={LABEL_FONT_SIZE}
      className="fill-foreground"
    >
      {name}
      <tspan dx={LABEL_OFFSET} className="fill-muted-foreground">
        {count}
      </tspan>
    </text>
  )
}
